import React from 'react';
import Helmet from 'react-helmet';

const ConflictsOfInterest = () => {
  return (
    <React.Fragment>
      <Helmet>
        <title>Research Conflicts of Interest | Author Guide | Cureus | Financial Relationships | Medical Journal Conflict of Interest</title>
        <meta name="description" content="Ensure transparency by declaring all research conflicts of interest when submitting to Cureus. Complete your conflict of interest disclosure statement today." />
      </Helmet>
      <h1>Conflicts of Interest</h1>
      <p>
        Cureus requires all parties involved in the publication process (all authors, reviewers, academic editors, and post-publication <a href='/siq'>SIQ scorers</a>) to transparently and fully declare any potential conflicts of interest (COI) related to their research, including all financial relationships.
      </p>
      <p>
        To prevent a potential conflict of interest, <strong>peer reviewers are prevented from reviewing the manuscript of authors employed by the same organization</strong>.
      </p>
      <p>
        Articles submitted through <strong>Cureus Academic Channels</strong> are assessed by Channel Curators and qualified Channel Administrators during two distinct steps in the article publication process. Articles are then passed to Cureus editors where they undergo rigorous review culminating in either approval or rejection. The decision to approve or reject is made independently of the earlier assessment performed by the channel team.
      </p>
      <h2>Requirements for Conflict Disclosure</h2>
      <ul className='disc indented-for-medium-up'>
        <li>Authors are required to complete their conflict of interest disclosure statement during the <a href='/author_guide#!/author-instructions/submitting-an-article'>submission process</a>.</li>
        <li>Reviewers are expected to consider any potential conflicts of interest before agreeing to review and to declare, in detail, any relevant conflicts of interest before submitting their review.</li>
        <li>Editors are expected to recuse themselves from the publication process if they feel they have conflicts of interest in regard to the research.</li>
      </ul>
      <h2>Editorial Board Submission Policies</h2>
      <p>
        <a href='/editorial_board'>Cureus Editorial Board</a> members and <a href='/associate_editors'>associate editors</a> are welcome to submit articles to Cureus. However, they may have absolutely no part in the handling of their manuscript at any stage of the publication process. While we recognize that eliminating all bias is impossible, we ensure every effort is made to minimize any bias during the evaluation and processing of these submissions.
      </p>
      <h2>Types of Conflicts to Report</h2>
      <p>
        All potential conflicts of interest should be declared during article submission to our journal. Please disclose a relationship and/or conflict of interest if faced with even the slightest amount of uncertainty as to its potential relevance.
      </p>
      <p>Examples of conflicts of interest in research articles include, but are not limited to: </p>
      <ul className='disc indented-for-medium-up'>
        <li>Possible financial benefits if the article is published</li>
        <li>Prior working or personal relationships with any of the authors</li>
        <li>Patent or grant activity that could be considered broadly relevant to the work</li>
        <li>Personal material or financial gain (such as free travel, gifts, etc.) relating to the work</li>
      </ul>
      <h2>Funding Source Declaration Guidelines</h2>
      <p>
        A complete list of funding sources (institutional, corporate, grants, etc.) and relevant relationships and financial activities must be detailed in the Ethics Statements and Conflict of Interest Disclosures section of the article submission process.
      </p>
      <p>
        The following conflicts of interest sections contain questions posed to the authors during the research article submission process. All COI sections must be addressed in order to satisfy our journal’s conflict of interest requirements. These sections are reprinted below as they appear in the article submission process. 
      </p>
      <p>
        The language contained in the four sections below is based on the <a href='http://www.icmje.org/conflicts-of-interest' target='_blank' rel='noreferrer'> ICMJE Form for Disclosure of Potential Conflicts of Interest</a>.
      </p>

      <h2>The Work Under Consideration for Publication</h2>
      <p>
        Did you or your institution at any time receive payment or services from a third party (government, commercial, private foundation, etc.) for any aspect of the submitted work (such as grants, data monitoring board, study design, manuscript preparation, statistical analysis, etc.)?
      </p>
      
      <h2>Relevant Financial Activities Outside the Submitted Work</h2>
      <p>
        Please indicate all financial relationships (regardless of compensation amount) associated with the <a href='/author_guide#!/author-instructions/author-roles'>submitting author</a> and all co-authors. You should disclose interactions with any entity that could be considered broadly relevant to your work. Report all relationships that were present during the 36 months prior to <a href='/about_publishing'>publication</a>. Remember – if there is any question, it is better to disclose a relationship than not to do so.
      </p>
      <p>
        For grants you have received for work outside the submitted work, only disclose support from entities that could be perceived to be affected financially by the published work, such as drug companies, or foundations supported by entities that could be perceived to have a financial stake in the outcome.
      </p>
      
      <h2>Intellectual Property: Patents &amp; Copyrights</h2>
      <p>
        Do you have any patents, whether planned, pending or issued, that could be considered broadly relevant to this work?
      </p>
      
      <h2>Relationships Not Covered Previously</h2>
      <p>
        Are there other relationships or activities that readers could perceive to have influenced, or that give the appearance of potentially influencing, what you wrote in the submitted work?
      </p>
      
      <h2>Disclosure of 3rd Party Funding</h2>
      <p>
        Separately from declaring any conflicts of interest, Cureus also requires that authors disclose any payments or services received from a 3rd party (government, commercial, private foundation, etc.) for any aspect of the submitted work (such as grants, data monitoring board, study design, article preparation, statistical analysis, etc.).
      </p>
      <p>
        This information is included in a disclosure statement attached to the final published article.
      </p>
    </React.Fragment>
  )
}

export default ConflictsOfInterest;